import { createAndResumeAudioContextWithRetry } from './audioContextUtils';
import { getWaveformComparisonConstants } from './waveformComparisonConstants';

interface IFetchArrayBufferResult {
  arrayBuffer: ArrayBuffer;
  objectUrl: string | null;
}

const {
  AVG_SPECTRUM_WEIGHT_DESKTOP,
  AVG_SPECTRUM_WEIGHT_MOBILE,
  DEFAULT_SIMILARITY_THRESHOLD,
  DOMINANT_FREQUENCY_ENERGY_THRESHOLD,
  DOMINANT_FREQUENCY_RELATIVE_THRESHOLD_MULTIPLIER,
  DOMINANT_FREQUENCY_WEIGHT_DESKTOP,
  DOMINANT_FREQUENCY_WEIGHT_MOBILE,
  FRAME_SIMILARITY_WEIGHT_DESKTOP,
  FRAME_SIMILARITY_WEIGHT_MOBILE,
  FREQUENCY_TOLERANCE_DESKTOP,
  FREQUENCY_TOLERANCE_MOBILE,
  HAMMING_WINDOW_CONSTANT,
  HAMMING_WINDOW_COSINE_COEFFICIENT,
  LOW_MOBILE_THRESHOLD_CUTOFF,
  MAX_FRAME_OFFSET_DESKTOP,
  MAX_FRAME_OFFSET_MOBILE,
  MAX_RMS_RATIO_LOW_MOBILE,
  MAX_RMS_RATIO_SAFARI,
  MAX_RMS_RATIO_STANDARD,
  MIN_AVG_SPECTRUM_SIMILARITY_DESKTOP,
  MIN_AVG_SPECTRUM_SIMILARITY_LOW_MOBILE,
  MIN_AVG_SPECTRUM_SIMILARITY_MOBILE,
  MIN_DOMINANT_FREQUENCY_SIMILARITY_DESKTOP,
  MIN_DOMINANT_FREQUENCY_SIMILARITY_LOW_MOBILE,
  MIN_DOMINANT_FREQUENCY_SIMILARITY_MOBILE,
  MIN_DURATION_RATIO_DESKTOP,
  MIN_DURATION_RATIO_LOW_MOBILE,
  MIN_DURATION_RATIO_MOBILE,
  MIN_FRAME_SIMILARITY_DESKTOP,
  MIN_FRAME_SIMILARITY_LOW_MOBILE,
  MIN_FRAME_SIMILARITY_MOBILE,
  MIN_MOBILE_SIMILARITY_THRESHOLD,
  MIN_OVERALL_SIMILARITY,
  MIN_RMS_FALLBACK,
  MIN_RMS_RATIO_LOW_MOBILE,
  MIN_RMS_RATIO_SAFARI,
  MIN_RMS_RATIO_STANDARD,
  MIN_RMS_THRESHOLD_DESKTOP,
  MIN_RMS_THRESHOLD_MOBILE,
  MIN_RMS_THRESHOLD_SAFARI,
  MIN_TOP_FREQUENCY_MATCHES_DESKTOP,
  MIN_TOP_FREQUENCY_MATCHES_LOW_MOBILE,
  MIN_TOP_FREQUENCY_MATCHES_MOBILE,
  MIN_TOP_FREQUENCY_MATCHES_SAFARI,
  MOBILE_SIMILARITY_THRESHOLD_ADJUSTMENT,
  NUM_DOMINANT_FREQUENCIES_TO_FIND,
  SAFARI_HIGH_SIMILARITY_THRESHOLD,
  SAFARI_RMS_FALLBACK_MULTIPLIER,
  SAMPLE_RATE_DIFFERENCE_THRESHOLD,
  DOMINANT_FREQ_SIMILARITY_LOW_THRESHOLD_DESKTOP,
  DOMINANT_FREQ_SIMILARITY_LOW_THRESHOLD_MOBILE,
  DOMINANT_FREQ_SIMILARITY_NORMAL_THRESHOLD_DESKTOP,
  DOMINANT_FREQ_SIMILARITY_NORMAL_THRESHOLD_MOBILE,
  DOMINANT_FREQ_BYPASS_MULTIPLIER,
  FFT_SIZE,
  LOW_THRESHOLD_DURATION_RATIO_MULTIPLIER,
  LOW_THRESHOLD_TOP_FREQ_MATCHES_MULTIPLIER,
  MIN_COMPONENT_SIMILARITY_FLOOR_LOW_THRESHOLD,
  MIN_DURATION_RATIO_FLOOR_LOW_THRESHOLD,
  MIN_THRESHOLD_SCALE_FOR_LOW_THRESHOLD,
  MIN_TOP_FREQUENCY_MATCHES_FLOOR_LOW_THRESHOLD,
  MIN_TOP_MATCHES_FOR_TOP2_VALIDATION,
  TOP_FREQUENCIES_COUNT_FOR_COMPARISON,
  TOP_FREQUENCIES_COUNT_FOR_VALIDATION,
  VERY_HIGH_SIMILARITY_BYPASS_MULTIPLIER,
} = getWaveformComparisonConstants();

/**
 * Fetches array buffer from URL string or Blob/File.
 */
async function fetchArrayBuffer(
  audioSource: string | Blob | File
): Promise<IFetchArrayBufferResult> {
  let objectUrl: string | null = null;

  if (typeof audioSource === 'string') {
    const response = await fetch(audioSource);
    const arrayBuffer = await response.arrayBuffer();

    return { arrayBuffer, objectUrl };
  }

  objectUrl = URL.createObjectURL(audioSource);
  const response = await fetch(objectUrl);
  const arrayBuffer = await response.arrayBuffer();

  return { arrayBuffer, objectUrl };
}

/**
 * Calculates the average spectrum from multiple frequency spectra.
 */
function calculateAverageSpectrum(spectra: Float32Array[]): Float32Array {
  if (spectra.length === 0) {
    return new Float32Array(0);
  }

  const avgSpectrum = new Float32Array(spectra[0]?.length || 0);

  spectra.forEach((spectrum) => {
    for (let i = 0; i < Math.min(avgSpectrum.length, spectrum.length); i += 1) {
      avgSpectrum[i] = (avgSpectrum[i] || 0) + (spectrum[i] || 0);
    }
  });

  for (let i = 0; i < avgSpectrum.length; i += 1) {
    const value = avgSpectrum[i];

    if (value !== undefined) {
      avgSpectrum[i] = value / spectra.length;
    }
  }

  return avgSpectrum;
}

/**
 * Calculates the magnitude of a vector.
 */
function calculateMagnitude(vector: Float32Array | number[]): number {
  let sum = 0;

  for (let i = 0; i < vector.length; i += 1) {
    const value = vector[i] || 0;
    sum += value * value;
  }

  return Math.sqrt(sum);
}

/**
 * Finds the maximum value in an array.
 */
export function findMax(arr: number[]): number {
  if (arr.length === 0) {
    return 0;
  }

  const firstValue = arr[0];

  if (firstValue === undefined) {
    return 0;
  }

  let max = firstValue;

  for (let i = 1; i < arr.length; i += 1) {
    const value = arr[i];

    if (value !== undefined && value > max) {
      max = value;
    }
  }

  return max;
}

/**
 * Finds the minimum value in an array.
 */
export function findMin(arr: number[]): number {
  if (arr.length === 0) {
    return 0;
  }

  const firstValue = arr[0];

  if (firstValue === undefined) {
    return 0;
  }

  let min = firstValue;

  for (let i = 1; i < arr.length; i += 1) {
    const value = arr[i];

    if (value !== undefined && value < min) {
      min = value;
    }
  }

  return min;
}

/**
 * Normalizes waveform data to a consistent length and amplitude range.
 */
export function normalizeWaveform(
  waveformData: Float32Array | number[],
  targetLength = 1000
): number[] {
  const data = Array.from(waveformData);

  if (data.length === 0) {
    return new Array(targetLength).fill(0);
  }

  let normalized: number[];

  if (data.length === targetLength) {
    normalized = [...data];
  } else if (data.length > targetLength) {
    const step = data.length / targetLength;
    normalized = Array.from({ length: targetLength }, (_, i) => {
      const index = Math.floor(i * step);

      return data[index] || 0;
    });
  } else {
    const step = (data.length - 1) / (targetLength - 1);

    normalized = Array.from({ length: targetLength }, (_, i) => {
      const position = i * step;
      const lowerIndex = Math.floor(position);
      const upperIndex = Math.min(lowerIndex + 1, data.length - 1);
      const fraction = position - lowerIndex;

      const lowerValue = data[lowerIndex] || 0;
      const upperValue = data[upperIndex] || 0;

      return lowerValue + (upperValue - lowerValue) * fraction;
    });
  }

  const absValues = normalized.map(Math.abs);
  const maxAmplitude = findMax(absValues);

  if (maxAmplitude === 0) {
    return normalized;
  }

  const result = normalized.map((value) => value / maxAmplitude);

  return result;
}

/**
 * Calculates normalized cross-correlation between two waveforms
 * and returns a similarity score between 0 and 1.
 */
export function compareWaveforms(
  waveform1: number[],
  waveform2: number[]
): number {
  if (waveform1.length === 0 || waveform2.length === 0) {
    return 0;
  }

  const minLength = Math.min(waveform1.length, waveform2.length);
  const w1 = waveform1.slice(0, minLength);
  const w2 = waveform2.slice(0, minLength);

  const mean1 = w1.reduce((sum, val) => sum + val, 0) / w1.length;
  const mean2 = w2.reduce((sum, val) => sum + val, 0) / w2.length;

  let denominator1 = 0;
  let denominator2 = 0;
  let numerator = 0;

  w1.forEach((val1, i) => {
    const val2 = w2[i] || 0;
    const diff1 = val1 - mean1;
    const diff2 = val2 - mean2;

    numerator += diff1 * diff2;
    denominator1 += diff1 * diff1;
    denominator2 += diff2 * diff2;
  });

  const denominator = Math.sqrt(denominator1 * denominator2);

  if (denominator === 0) {
    return 0;
  }

  const correlation = numerator / denominator;
  const similarity = (correlation + 1) / 2;

  return similarity;
}

/**
 * Detects if running on iOS device.
 */
function isIOSDevice(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * Detects if running on Safari browser (including iOS Safari and Mac Safari).
 */
function isSafari(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const { userAgent, vendor } = navigator;
  const isSafariUA =
    /Safari/i.test(userAgent) &&
    !/Chrome|CriOS|FxiOS|EdgiOS|Opera|OPR/i.test(userAgent) &&
    /Apple Computer|Apple/.test(vendor || '');

  return isSafariUA;
}

/**
 * Gets the supported MIME type for MediaRecorder based on browser capabilities.
 * Returns a default type if none are explicitly supported (browser will choose).
 * Handles iOS Safari limitations and provides fallbacks.
 */
export function getSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') {
    return undefined;
  }

  if (!MediaRecorder.isTypeSupported) {
    return undefined;
  }

  const isIOS = isIOSDevice();
  const isSafariBrowser = isSafari();

  if (isIOS || isSafariBrowser) {
    const safariMimeTypes = [
      'audio/mp4',
      'audio/aac',
      'audio/mpeg',
      'audio/mp4;codecs=mp4a.40.2',
      'audio/mp4;codecs=mp4a.40.5',
    ];

    const isSupported = (mimeType: string): boolean =>
      MediaRecorder.isTypeSupported(mimeType);

    const supportedSafariType = safariMimeTypes.find(isSupported);

    if (supportedSafariType) {
      return supportedSafariType;
    }
  }

  const mimeTypes = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
    'audio/aac',
  ];

  const isSupported = (mimeType: string): boolean =>
    MediaRecorder.isTypeSupported(mimeType);

  const supportedType = mimeTypes.find(isSupported);

  return supportedType;
}

/**
 * Extracts waveform data from AudioBuffer using the first channel.
 */
export function extractWaveformFromAudioBuffer(
  audioBuffer: AudioBuffer
): Float32Array {
  return audioBuffer.getChannelData(0);
}

/**
 * Extracts normalized waveform data from an audio file (URL or Blob/File).
 */
export async function extractWaveformFromAudio(
  audioSource: Blob | File | string,
  browserNotSupportedErrorMessage?: string
): Promise<number[]> {
  let audioContext: AudioContext | null = null;

  try {
    audioContext = await createAndResumeAudioContextWithRetry(
      browserNotSupportedErrorMessage
    );
    let objectUrl: string | null = null;

    const cleanup = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };

    try {
      const { arrayBuffer, objectUrl: url } = await fetchArrayBuffer(
        audioSource
      );
      objectUrl = url;
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const waveformData = extractWaveformFromAudioBuffer(audioBuffer);
      const normalized = normalizeWaveform(waveformData);
      cleanup();

      return normalized;
    } catch (error) {
      cleanup();
      throw error;
    }
  } finally {
    if (audioContext) {
      await audioContext.close();
    }
  }
}

/**
 * Normalizes audio samples to [-1, 1] range.
 */
export function normalizeAudioSamples(audioBuffer: AudioBuffer): Float32Array {
  const channelData = audioBuffer.getChannelData(0);
  const samples = new Float32Array(channelData.length);

  if (channelData.length === 0) {
    return samples;
  }

  let maxAmplitude = 0;

  for (let i = 0; i < channelData.length; i += 1) {
    const absValue = Math.abs(channelData[i] || 0);

    if (absValue > maxAmplitude) {
      maxAmplitude = absValue;
    }
  }

  if (maxAmplitude > 0) {
    for (let i = 0; i < channelData.length; i += 1) {
      samples[i] = (channelData[i] || 0) / maxAmplitude;
    }
  }

  return samples;
}

/**
 * Computes FFT (Fast Fourier Transform) using Cooley-Tukey algorithm.
 */
function computeFFT(samples: Float32Array): {
  imag: Float32Array;
  real: Float32Array;
} {
  const N = samples.length;
  const imag = new Float32Array(N);
  const real = new Float32Array(N);

  for (let i = 0; i < N; i += 1) {
    real[i] = samples[i] || 0;
    imag[i] = 0;
  }

  let j = 0;

  // eslint-disable-next-line no-bitwise
  for (let i = 1; i < N; i += 1) {
    // eslint-disable-next-line no-bitwise
    let bit = N >> 1;

    // eslint-disable-next-line no-bitwise
    while (j & bit) {
      // eslint-disable-next-line no-bitwise
      j ^= bit;
      // eslint-disable-next-line no-bitwise
      bit >>= 1;
    }

    // eslint-disable-next-line no-bitwise
    j ^= bit;

    if (i < j) {
      const tempImag = imag[i];
      const tempReal = real[i];

      imag[i] = imag[j] || 0;
      real[i] = real[j] || 0;
      imag[j] = tempImag || 0;
      real[j] = tempReal || 0;
    }
  }

  // eslint-disable-next-line no-bitwise
  for (let len = 2; len <= N; len <<= 1) {
    const angle = (-2 * Math.PI) / len;
    const wlenImag = Math.sin(angle);
    const wlenReal = Math.cos(angle);

    for (let i = 0; i < N; i += len) {
      let wImag = 0;
      let wReal = 1;

      for (let k = 0; k < len / 2; k += 1) {
        const uImag = imag[i + k] || 0;
        const uReal = real[i + k] || 0;
        const vImag =
          (real[i + k + len / 2] || 0) * wImag +
          (imag[i + k + len / 2] || 0) * wReal;
        const vReal =
          (real[i + k + len / 2] || 0) * wReal -
          (imag[i + k + len / 2] || 0) * wImag;

        imag[i + k] = uImag + vImag;
        real[i + k] = uReal + vReal;
        imag[i + k + len / 2] = uImag - vImag;
        real[i + k + len / 2] = uReal - vReal;

        const nextWImag = wReal * wlenImag + wImag * wlenReal;
        const nextWReal = wReal * wlenReal - wImag * wlenImag;
        wImag = nextWImag;
        wReal = nextWReal;
      }
    }
  }

  return { imag, real };
}

/**
 * Computes power spectrum from FFT result.
 */
function computePowerSpectrum(fftResult: {
  imag: Float32Array;
  real: Float32Array;
}): Float32Array {
  const N = fftResult.real.length;
  const powerSpectrum = new Float32Array(N / 2);

  for (let i = 0; i < N / 2; i += 1) {
    const imag = fftResult.imag[i] || 0;
    const real = fftResult.real[i] || 0;
    powerSpectrum[i] = real * real + imag * imag;
  }

  return powerSpectrum;
}

/**
 * Applies Hamming window to audio frame.
 */
function applyHammingWindow(frame: Float32Array): Float32Array {
  const N = frame.length;
  const windowed = new Float32Array(N);

  for (let i = 0; i < N; i += 1) {
    const windowValue =
      HAMMING_WINDOW_CONSTANT -
      HAMMING_WINDOW_COSINE_COEFFICIENT * Math.cos((2 * Math.PI * i) / (N - 1));
    windowed[i] = (frame[i] || 0) * windowValue;
  }

  return windowed;
}

/**
 * Converts frequency to Mel scale.
 */
function hzToMel(frequencyHz: number): number {
  return 2595 * Math.log10(1 + frequencyHz / 700);
}

/**
 * Converts Mel scale to frequency.
 */
function melToHz(mel: number): number {
  return 700 * (10 ** (mel / 2595) - 1);
}

/**
 * Creates Mel filter bank.
 */
function createMelFilterBank(
  fftSize: number,
  numFilters: number,
  sampleRate: number
): number[][] {
  const nyquist = sampleRate / 2;
  const highMel = hzToMel(nyquist);
  const lowMel = hzToMel(0);
  const melStep = (highMel - lowMel) / (numFilters + 1);
  const filters: number[][] = [];

  for (let i = 0; i < numFilters; i += 1) {
    const filter = new Array(fftSize / 2).fill(0);
    const centerMel = lowMel + (i + 1) * melStep;
    const centerHz = melToHz(centerMel);
    const centerBin = Math.floor((centerHz / nyquist) * (fftSize / 2));

    const leftMel = lowMel + i * melStep;
    const leftHz = melToHz(leftMel);
    const leftBin = Math.floor((leftHz / nyquist) * (fftSize / 2));

    const rightMel = lowMel + (i + 2) * melStep;
    const rightHz = melToHz(rightMel);
    const rightBin = Math.floor((rightHz / nyquist) * (fftSize / 2));

    for (let j = leftBin; j <= centerBin; j += 1) {
      if (j >= 0 && j < filter.length) {
        filter[j] = (j - leftBin) / (centerBin - leftBin || 1);
      }
    }

    for (let j = centerBin; j <= rightBin; j += 1) {
      if (j >= 0 && j < filter.length) {
        filter[j] = (rightBin - j) / (rightBin - centerBin || 1);
      }
    }

    filters.push(filter);
  }

  return filters;
}

/**
 * Computes DCT (Discrete Cosine Transform).
 */
function computeDCT(input: number[], numCoefficients: number): number[] {
  const N = input.length;
  const coefficients: number[] = [];

  for (let k = 0; k < numCoefficients; k += 1) {
    let sum = 0;

    for (let n = 0; n < N; n += 1) {
      const value = input[n] || 0;
      sum += value * Math.cos((Math.PI * k * (2 * n + 1)) / (2 * N));
    }

    const scale = k === 0 ? Math.sqrt(1 / N) : Math.sqrt(2 / N);
    coefficients.push(scale * sum);
  }

  return coefficients;
}

/**
 * Extracts MFCC features from normalized audio samples using pure Web Audio API.
 */
export function extractMFCCFeatures(
  normalizedSamples: Float32Array,
  sampleRate: number
): number[][] {
  const bufferSize = 512;
  const hopSize = 256;
  const mfccFrames: number[][] = [];
  const numMelFilters = 26;
  const numMFCCCoefficients = 13;

  if (normalizedSamples.length < bufferSize) {
    return [];
  }

  const melFilterBank = createMelFilterBank(
    bufferSize,
    numMelFilters,
    sampleRate
  );

  for (let i = 0; i <= normalizedSamples.length - bufferSize; i += hopSize) {
    const frame = normalizedSamples.slice(i, i + bufferSize);

    if (frame.length < bufferSize) {
      break;
    }

    const windowedFrame = applyHammingWindow(frame);
    const fftResult = computeFFT(windowedFrame);
    const powerSpectrum = computePowerSpectrum(fftResult);
    const melEnergies: number[] = [];

    for (let filterIdx = 0; filterIdx < numMelFilters; filterIdx += 1) {
      let energy = 0;
      const filter = melFilterBank[filterIdx] || [];

      for (let j = 0; j < filter.length; j += 1) {
        energy += (powerSpectrum[j] || 0) * (filter[j] || 0);
      }

      melEnergies.push(Math.log(energy + 1e-10));
    }

    const mfccCoefficients = computeDCT(melEnergies, numMFCCCoefficients);

    mfccFrames.push(mfccCoefficients);
  }

  return mfccFrames;
}

/**
 * Averages MFCC vectors across all frames.
 */
export function averageMFCCVectors(mfccFrames: number[][]): number[] {
  if (mfccFrames.length === 0) {
    return [];
  }

  const numCoefficients = mfccFrames[0]?.length || 0;

  if (numCoefficients === 0) {
    return [];
  }

  const averaged = new Array(numCoefficients).fill(0);

  for (let i = 0; i < mfccFrames.length; i += 1) {
    const frame = mfccFrames[i];

    if (frame) {
      for (let j = 0; j < numCoefficients; j += 1) {
        const value = frame[j];

        if (value !== undefined) {
          averaged[j] += value;
        }
      }
    }
  }

  for (let j = 0; j < numCoefficients; j += 1) {
    averaged[j] /= mfccFrames.length;
  }

  return averaged;
}

/**
 * Calculates cosine similarity between two vectors.
 */
export function cosineSimilarity(vecA: number[], vecB: number[]): number {
  if (vecA.length === 0 || vecB.length === 0) {
    return 0;
  }

  const minLength = Math.min(vecA.length, vecB.length);
  const a = vecA.slice(0, minLength);
  const b = vecB.slice(0, minLength);

  let dotProduct = 0;

  for (let i = 0; i < minLength; i += 1) {
    const valA = a[i] || 0;
    const valB = b[i] || 0;
    dotProduct += valA * valB;
  }

  const magnitudeA = calculateMagnitude(a);
  const magnitudeB = calculateMagnitude(b);

  if (magnitudeA === 0 || magnitudeB === 0) {
    return 0;
  }

  return dotProduct / (magnitudeA * magnitudeB);
}

/**
 * Decodes audio from URL string or Blob/File using Web Audio API.
 */
async function decodeAudioData(
  audioContext: AudioContext,
  audioSource: Blob | File | string
): Promise<AudioBuffer> {
  let objectUrl: string | null = null;

  try {
    if (!audioContext.decodeAudioData) {
      throw new Error('decodeAudioData is not supported in this browser');
    }

    const { arrayBuffer, objectUrl: url } = await fetchArrayBuffer(audioSource);
    objectUrl = url;

    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }

    return audioBuffer;
  } catch (error) {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }

    throw error;
  }
}

/**
 * Computes chroma features from power spectrum representing the 12 pitch classes.
 */
function computeChroma(
  powerSpectrum: Float32Array,
  sampleRate: number
): number[] {
  const nyquist = sampleRate / 2;
  const chroma = new Array(12).fill(0);
  const binWidth = nyquist / powerSpectrum.length;
  const numBins = powerSpectrum.length;

  for (let i = 0; i < numBins; i += 1) {
    const frequency = i * binWidth;

    if (frequency > 0) {
      const midiNote = 12 * Math.log2(frequency / 440) + 69;
      const chromaClass = Math.round(midiNote) % 12;
      chroma[chromaClass] += powerSpectrum[i] || 0;
    }
  }

  const sum = chroma.reduce((acc, val) => acc + val, 0);

  if (sum > 0) {
    for (let i = 0; i < chroma.length; i += 1) {
      chroma[i] /= sum;
    }
  }

  return chroma;
}

/**
 * Computes spectral centroid representing the brightness of sound.
 */
function computeSpectralCentroid(
  powerSpectrum: Float32Array,
  sampleRate: number
): number {
  const nyquist = sampleRate / 2;
  const binWidth = nyquist / powerSpectrum.length;
  const numBins = powerSpectrum.length;
  let magnitudeSum = 0;
  let weightedSum = 0;

  for (let i = 0; i < numBins; i += 1) {
    const frequency = i * binWidth;
    const magnitude = powerSpectrum[i] || 0;

    magnitudeSum += magnitude;
    weightedSum += frequency * magnitude;
  }

  return magnitudeSum > 0 ? weightedSum / magnitudeSum : 0;
}

/**
 * Computes spectral rolloff representing the frequency below which 85% of energy is contained.
 */
function computeSpectralRolloff(
  powerSpectrum: Float32Array,
  sampleRate: number
): number {
  const nyquist = sampleRate / 2;
  const binWidth = nyquist / powerSpectrum.length;
  const numBins = powerSpectrum.length;
  const threshold = DOMINANT_FREQUENCY_ENERGY_THRESHOLD;

  let totalEnergy = 0;

  for (let i = 0; i < numBins; i += 1) {
    totalEnergy += powerSpectrum[i] || 0;
  }

  if (totalEnergy === 0) {
    return 0;
  }

  let cumulativeEnergy = 0;

  for (let i = 0; i < numBins; i += 1) {
    cumulativeEnergy += powerSpectrum[i] || 0;

    if (cumulativeEnergy >= threshold * totalEnergy) {
      return i * binWidth;
    }
  }

  return nyquist;
}

/**
 * Computes spectral flux representing the rate of change of spectrum.
 */
function computeSpectralFlux(
  currentPowerSpectrum: Float32Array,
  previousPowerSpectrum?: Float32Array
): number {
  if (!previousPowerSpectrum) {
    return 0;
  }

  let flux = 0;
  const minLength = Math.min(
    currentPowerSpectrum.length,
    previousPowerSpectrum.length
  );

  for (let i = 0; i < minLength; i += 1) {
    const diff =
      (currentPowerSpectrum[i] || 0) - (previousPowerSpectrum[i] || 0);

    if (diff > 0) {
      flux += diff;
    }
  }

  return flux;
}

/**
 * Extracts spectral features from audio frames using pure Web Audio API.
 */
export function extractSpectralFeatures(
  normalizedSamples: Float32Array,
  sampleRate: number
): number[][] {
  const bufferSize = 512;
  const hopSize = 256;
  const spectralFrames: number[][] = [];

  if (normalizedSamples.length < bufferSize) {
    return [];
  }

  let previousPowerSpectrum: Float32Array | undefined;

  for (let i = 0; i <= normalizedSamples.length - bufferSize; i += hopSize) {
    const frame = normalizedSamples.slice(i, i + bufferSize);

    if (frame.length < bufferSize) {
      break;
    }

    const windowedFrame = applyHammingWindow(frame);
    const fftResult = computeFFT(windowedFrame);
    const powerSpectrum = computePowerSpectrum(fftResult);
    const spectralFeature: number[] = [];

    const chroma = computeChroma(powerSpectrum, sampleRate);
    spectralFeature.push(...chroma);

    const centroid = computeSpectralCentroid(powerSpectrum, sampleRate);
    spectralFeature.push(centroid / sampleRate);

    const rolloff = computeSpectralRolloff(powerSpectrum, sampleRate);
    spectralFeature.push(rolloff / sampleRate);

    const flux = computeSpectralFlux(powerSpectrum, previousPowerSpectrum);
    spectralFeature.push(flux);

    spectralFrames.push(spectralFeature);
    previousPowerSpectrum = powerSpectrum;
  }

  return spectralFrames;
}

/**
 * Extracts frequency spectrum frames from audio samples.
 */
function extractFrequencySpectrum(
  fftSize: number,
  normalizedSamples: Float32Array,
  sampleRate: number
): Float32Array[] {
  // sampleRate parameter is kept for API consistency but not used in this function
  // eslint-disable-next-line no-void
  void sampleRate;
  const hopSize = fftSize / 4;
  const spectra: Float32Array[] = [];

  for (let i = 0; i <= normalizedSamples.length - fftSize; i += hopSize) {
    const frame = normalizedSamples.slice(i, i + fftSize);

    if (frame.length < fftSize) {
      break;
    }

    const windowedFrame = applyHammingWindow(frame);
    const fftResult = computeFFT(windowedFrame);
    const powerSpectrum = computePowerSpectrum(fftResult);

    spectra.push(powerSpectrum);
  }

  return spectra;
}

/**
 * Applies smoothing to power spectrum to reduce noise sensitivity.
 */
function smoothPowerSpectrum(
  spectrum: Float32Array,
  windowSize = 3
): Float32Array {
  const smoothed = new Float32Array(spectrum.length);
  const halfWindow = Math.floor(windowSize / 2);

  for (let i = 0; i < spectrum.length; i += 1) {
    let sum = 0;
    let count = 0;

    const startValues = [0, i - halfWindow];
    let startValue = 0;

    for (let k = 0; k < startValues.length; k += 1) {
      const v = startValues[k];

      if (v !== undefined && Number.isFinite(v) && v > startValue) {
        startValue = v;
      }
    }

    for (
      let j = startValue;
      j <= Math.min(spectrum.length - 1, i + halfWindow);
      j += 1
    ) {
      sum += spectrum[j] || 0;
      count += 1;
    }

    smoothed[i] = count > 0 ? sum / count : spectrum[i] || 0;
  }

  return smoothed;
}

/**
 * Compares two frequency spectra directly by comparing frequency bins.
 * Uses relative magnitude comparison to be volume-independent.
 */
function compareFrequencySpectra(
  spectrum1: Float32Array,
  spectrum2: Float32Array
): number {
  const minLength = Math.min(spectrum1.length, spectrum2.length);

  if (minLength === 0) {
    return 0;
  }

  const smoothedSpectrum1 = smoothPowerSpectrum(spectrum1);
  const smoothedSpectrum2 = smoothPowerSpectrum(spectrum2);

  const magnitude1 = calculateMagnitude(smoothedSpectrum1);
  const magnitude2 = calculateMagnitude(smoothedSpectrum2);

  if (magnitude1 === 0 || magnitude2 === 0) {
    return 0;
  }

  let dotProduct = 0;

  for (let i = 0; i < minLength; i += 1) {
    const val1 = (smoothedSpectrum1[i] || 0) / magnitude1;
    const val2 = (smoothedSpectrum2[i] || 0) / magnitude2;
    dotProduct += val1 * val2;
  }

  return (dotProduct + 1) / 2;
}

/**
 * Finds dominant frequencies in a spectrum.
 * Uses relative magnitude threshold instead of absolute to be volume-independent.
 */
function findDominantFrequencies(
  numPeaks: number,
  powerSpectrum: Float32Array,
  sampleRate: number
): number[] {
  const nyquist = sampleRate / 2;
  const binWidth = nyquist / powerSpectrum.length;
  const frequencies: { frequency: number; magnitude: number }[] = [];

  const smoothedSpectrum = smoothPowerSpectrum(powerSpectrum);
  let maxMagnitude = 0;

  for (let i = 0; i < smoothedSpectrum.length; i += 1) {
    const v = smoothedSpectrum[i];

    if (v !== undefined && Number.isFinite(v) && v > maxMagnitude) {
      maxMagnitude = v;
    }
  }

  const relativeThreshold =
    maxMagnitude * DOMINANT_FREQUENCY_RELATIVE_THRESHOLD_MULTIPLIER;

  for (let i = 1; i < smoothedSpectrum.length - 1; i += 1) {
    const magnitude = smoothedSpectrum[i] || 0;
    const nextMagnitude = smoothedSpectrum[i + 1] || 0;
    const prevMagnitude = smoothedSpectrum[i - 1] || 0;

    if (
      magnitude > nextMagnitude &&
      magnitude > prevMagnitude &&
      magnitude > relativeThreshold
    ) {
      const frequency = i * binWidth;
      frequencies.push({ frequency, magnitude });
    }
  }

  frequencies.sort((a, b) => b.magnitude - a.magnitude);

  return frequencies.slice(0, numPeaks).map((f) => f.frequency);
}

/**
 * Compares dominant frequencies between two spectra.
 * Returns similarity score based on how many top frequencies match.
 */
function compareDominantFrequencies(
  frequencies1: number[],
  frequencies2: number[],
  toleranceHz: number
): number {
  if (frequencies1.length === 0 || frequencies2.length === 0) {
    return 0;
  }

  const topN = Math.min(
    TOP_FREQUENCIES_COUNT_FOR_COMPARISON,
    Math.min(frequencies1.length, frequencies2.length)
  );
  const topFreqs1 = frequencies1.slice(0, topN);
  const topFreqs2 = frequencies2.slice(0, topN);

  let matches = 0;
  const usedIndices = new Set<number>();

  topFreqs1.forEach((freq1) => {
    let bestDiff = Infinity;
    let bestMatch = -1;

    for (let j = 0; j < topFreqs2.length; j += 1) {
      if (!usedIndices.has(j)) {
        const freq2 = topFreqs2[j] || 0;
        const diff = Math.abs(freq1 - freq2);

        if (diff <= toleranceHz && diff < bestDiff) {
          bestDiff = diff;
          bestMatch = j;
        }
      }
    }

    if (bestMatch >= 0) {
      matches += 1;
      usedIndices.add(bestMatch);
    }
  });

  return matches / topN;
}

/**
 * Validates that top frequencies match closely (for exact audio matching).
 * Requires the top frequencies to match in order of importance.
 */
function validateTopFrequenciesMatch(
  frequencies1: number[],
  frequencies2: number[],
  toleranceHz: number,
  minTopMatches: number
): boolean {
  if (frequencies1.length === 0 || frequencies2.length === 0) {
    return false;
  }

  const topCount = Math.min(
    TOP_FREQUENCIES_COUNT_FOR_VALIDATION,
    Math.min(frequencies1.length, frequencies2.length)
  );
  const topFreqs1 = frequencies1.slice(0, topCount);
  const topFreqs2 = frequencies2.slice(0, topCount);

  let matches = 0;
  const usedIndices = new Set<number>();

  topFreqs1.forEach((freq1) => {
    let bestMatch = -1;
    let bestDiff = Infinity;

    for (let j = 0; j < topFreqs2.length; j += 1) {
      if (!usedIndices.has(j)) {
        const freq2 = topFreqs2[j] || 0;
        const diff = Math.abs(freq1 - freq2);

        if (diff <= toleranceHz && diff < bestDiff) {
          bestDiff = diff;
          bestMatch = j;
        }
      }
    }

    if (bestMatch >= 0) {
      matches += 1;
      usedIndices.add(bestMatch);
    }
  });

  const top2MustMatch =
    topFreqs1.length >= 2 &&
    topFreqs2.length >= 2 &&
    minTopMatches >= MIN_TOP_MATCHES_FOR_TOP2_VALIDATION;
  let top2Match = true;

  if (top2MustMatch) {
    const top1Match =
      Math.abs((topFreqs1[0] || 0) - (topFreqs2[0] || 0)) <= toleranceHz;
    const top2MatchDirect =
      Math.abs((topFreqs1[1] || 0) - (topFreqs2[1] || 0)) <= toleranceHz;
    const top2MatchCross =
      Math.abs((topFreqs1[0] || 0) - (topFreqs2[1] || 0)) <= toleranceHz &&
      Math.abs((topFreqs1[1] || 0) - (topFreqs2[0] || 0)) <= toleranceHz;

    top2Match = top1Match || top2MatchDirect || top2MatchCross;
  }

  const hasMinimumMatches = matches >= minTopMatches;
  const passesValidation =
    hasMinimumMatches && (top2MustMatch ? top2Match : true);

  return passesValidation;
}

/**
 * Resamples audio buffer to target sample rate for consistent comparison.
 * Falls back to linear interpolation if OfflineAudioContext is not available.
 */
async function resampleAudioBuffer(
  audioBuffer: AudioBuffer,
  targetSampleRate: number,
  browserNotSupportedErrorMessage?: string
): Promise<AudioBuffer> {
  const sourceSampleRate = audioBuffer.sampleRate;

  if (sourceSampleRate === targetSampleRate) {
    return audioBuffer;
  }

  if (typeof OfflineAudioContext === 'undefined') {
    const ratio = sourceSampleRate / targetSampleRate;
    const { numberOfChannels } = audioBuffer;
    const newLength = Math.round(audioBuffer.length / ratio);
    let audioContext: AudioContext | null = null;

    try {
      audioContext = await createAndResumeAudioContextWithRetry(
        browserNotSupportedErrorMessage
      );
      const newBuffer = audioContext.createBuffer(
        numberOfChannels,
        newLength,
        targetSampleRate
      );

      for (let channel = 0; channel < numberOfChannels; channel += 1) {
        const inputData = audioBuffer.getChannelData(channel);
        const outputData = newBuffer.getChannelData(channel);

        for (let i = 0; i < newLength; i += 1) {
          const srcIndex = i * ratio;
          const srcIndexFloor = Math.floor(srcIndex);
          const srcIndexCeil = Math.min(
            srcIndexFloor + 1,
            inputData.length - 1
          );
          const fraction = srcIndex - srcIndexFloor;

          const value1 = inputData[srcIndexFloor] || 0;
          const value2 = inputData[srcIndexCeil] || 0;

          outputData[i] = value1 + (value2 - value1) * fraction;
        }
      }

      return newBuffer;
    } finally {
      if (audioContext) {
        await audioContext.close();
      }
    }
  }

  const ratio = sourceSampleRate / targetSampleRate;
  const { numberOfChannels } = audioBuffer;
  const length = Math.round(audioBuffer.length / ratio);
  const offlineContext = new OfflineAudioContext(
    numberOfChannels,
    length,
    targetSampleRate
  );
  const sourceBuffer = offlineContext.createBuffer(
    numberOfChannels,
    audioBuffer.length,
    sourceSampleRate
  );

  for (let channel = 0; channel < numberOfChannels; channel += 1) {
    const inputData = audioBuffer.getChannelData(channel);
    const outputData = sourceBuffer.getChannelData(channel);

    for (let i = 0; i < inputData.length; i += 1) {
      outputData[i] = inputData[i] || 0;
    }
  }

  const bufferSource = offlineContext.createBufferSource();

  bufferSource.buffer = sourceBuffer;
  bufferSource.connect(offlineContext.destination);
  bufferSource.start(0);

  const renderedBuffer = await offlineContext.startRendering();
  return renderedBuffer;
}

/**
 * Detects if running on a mobile device.
 */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

export interface IComparisonResult {
  avgSpectrumSimilarity: number;
  bestFrameSimilarity: number;
  dominantFreqSimilarity: number;
  durationRatio: number;
  isSimilar: boolean;
  recordedRMS: number;
  similarityScore: number;
}

/**
 * Compares two audio files using exact frequency spectrum comparison.
 * Handles mobile device differences including sample rate variations,
 * timing differences, and hardware-specific audio characteristics.
 * Returns detailed comparison results including similarity score.
 */
export async function compareAudioWithMFCC(
  referenceAudio: Blob | File | string,
  recordedAudio: Blob | File,
  mobileSimilarityThreshold: number | undefined,
  browserNotSupportedErrorMessage: string | undefined,
  similarityThreshold = DEFAULT_SIMILARITY_THRESHOLD
): Promise<IComparisonResult> {
  let audioContext: AudioContext | null = null;

  try {
    audioContext = await createAndResumeAudioContextWithRetry(
      browserNotSupportedErrorMessage
    );
    const isMobile = isMobileDevice();
    const isLowMobileThreshold =
      isMobile &&
      mobileSimilarityThreshold !== undefined &&
      mobileSimilarityThreshold < LOW_MOBILE_THRESHOLD_CUTOFF;

    const [referenceBuffer, recordedBuffer] = await Promise.all([
      decodeAudioData(audioContext, referenceAudio),
      decodeAudioData(audioContext, recordedAudio),
    ]);

    const fftSize = FFT_SIZE;
    const referenceSampleRate = referenceBuffer.sampleRate;
    const recordedSampleRate = recordedBuffer.sampleRate;

    let processedReferenceBuffer = referenceBuffer;
    let processedRecordedBuffer = recordedBuffer;
    let targetSampleRate = referenceSampleRate;

    if (
      Math.abs(referenceSampleRate - recordedSampleRate) >
      SAMPLE_RATE_DIFFERENCE_THRESHOLD
    ) {
      targetSampleRate = Math.min(referenceSampleRate, recordedSampleRate);

      if (referenceSampleRate !== targetSampleRate) {
        processedReferenceBuffer = await resampleAudioBuffer(
          referenceBuffer,
          targetSampleRate,
          browserNotSupportedErrorMessage
        );
      }

      if (recordedSampleRate !== targetSampleRate) {
        processedRecordedBuffer = await resampleAudioBuffer(
          recordedBuffer,
          targetSampleRate,
          browserNotSupportedErrorMessage
        );
      }
    } else {
      targetSampleRate = Math.min(referenceSampleRate, recordedSampleRate);
    }

    const recordedChannelData = processedRecordedBuffer.getChannelData(0);
    let recordedEnergy = 0;

    for (let i = 0; i < recordedChannelData.length; i += 1) {
      const sample = recordedChannelData[i] || 0;
      recordedEnergy += sample * sample;
    }

    const recordedRMS = Math.sqrt(recordedEnergy / recordedChannelData.length);

    const referenceChannelData = processedReferenceBuffer.getChannelData(0);
    let referenceEnergy = 0;

    for (let i = 0; i < referenceChannelData.length; i += 1) {
      const sample = referenceChannelData[i] || 0;
      referenceEnergy += sample * sample;
    }

    const referenceRMS = Math.sqrt(
      referenceEnergy / referenceChannelData.length
    );

    const isSafariBrowser = isSafari();
    let minRMSThreshold: number;

    if (isSafariBrowser) {
      minRMSThreshold = MIN_RMS_THRESHOLD_SAFARI;
    } else if (isMobile) {
      minRMSThreshold = MIN_RMS_THRESHOLD_MOBILE;
    } else {
      minRMSThreshold = MIN_RMS_THRESHOLD_DESKTOP;
    }

    if (recordedRMS < minRMSThreshold) {
      if (
        isSafariBrowser &&
        recordedRMS > minRMSThreshold * SAFARI_RMS_FALLBACK_MULTIPLIER &&
        referenceRMS > 0 &&
        recordedRMS / referenceRMS >= MIN_RMS_RATIO_SAFARI
      ) {
        // Safari fallback condition met, continue processing
      } else {
        return {
          avgSpectrumSimilarity: 0,
          bestFrameSimilarity: 0,
          dominantFreqSimilarity: 0,
          durationRatio: 0,
          isSimilar: false,
          recordedRMS,
          similarityScore: 0,
        };
      }
    }

    const rmsRatio = recordedRMS / (referenceRMS || MIN_RMS_FALLBACK);
    let minRMSRatio: number;

    if (isSafariBrowser) {
      minRMSRatio = MIN_RMS_RATIO_SAFARI;
    } else if (isLowMobileThreshold) {
      minRMSRatio = MIN_RMS_RATIO_LOW_MOBILE;
    } else {
      minRMSRatio = MIN_RMS_RATIO_STANDARD;
    }

    let maxRMSRatio: number;

    if (isSafariBrowser) {
      maxRMSRatio = MAX_RMS_RATIO_SAFARI;
    } else if (isLowMobileThreshold) {
      maxRMSRatio = MAX_RMS_RATIO_LOW_MOBILE;
    } else {
      maxRMSRatio = MAX_RMS_RATIO_STANDARD;
    }

    if (rmsRatio < minRMSRatio || rmsRatio > maxRMSRatio) {
      return {
        avgSpectrumSimilarity: 0,
        bestFrameSimilarity: 0,
        dominantFreqSimilarity: 0,
        durationRatio: 0,
        isSimilar: false,
        recordedRMS,
        similarityScore: 0,
      };
    }

    const normalizedProcessedRecorded = normalizeAudioSamples(
      processedRecordedBuffer
    );
    const normalizedProcessedReference = normalizeAudioSamples(
      processedReferenceBuffer
    );

    const recordedSpectra = extractFrequencySpectrum(
      fftSize,
      normalizedProcessedRecorded,
      targetSampleRate
    );
    const referenceSpectra = extractFrequencySpectrum(
      fftSize,
      normalizedProcessedReference,
      targetSampleRate
    );

    if (referenceSpectra.length === 0 || recordedSpectra.length === 0) {
      return {
        avgSpectrumSimilarity: 0,
        bestFrameSimilarity: 0,
        dominantFreqSimilarity: 0,
        durationRatio: 0,
        isSimilar: false,
        recordedRMS,
        similarityScore: 0,
      };
    }

    const longerSpectra =
      referenceSpectra.length > recordedSpectra.length
        ? referenceSpectra
        : recordedSpectra;
    const shorterSpectra =
      referenceSpectra.length <= recordedSpectra.length
        ? referenceSpectra
        : recordedSpectra;

    const maxOffset = Math.min(
      longerSpectra.length - shorterSpectra.length,
      isMobile ? MAX_FRAME_OFFSET_MOBILE : MAX_FRAME_OFFSET_DESKTOP
    );
    let bestFrameSimilarity = -1;

    for (let offset = 0; offset <= maxOffset; offset += 1) {
      let totalSimilarity = 0;
      let validFrames = 0;

      for (let i = 0; i < shorterSpectra.length; i += 1) {
        const longerFrame = longerSpectra[i + offset];

        if (longerFrame) {
          const frameSimilarity = compareFrequencySpectra(
            shorterSpectra[i] || new Float32Array(0),
            longerFrame
          );
          totalSimilarity += frameSimilarity;
          validFrames += 1;
        }
      }

      if (validFrames > 0) {
        const avgSimilarity = totalSimilarity / validFrames;

        if (avgSimilarity > bestFrameSimilarity) {
          bestFrameSimilarity = avgSimilarity;
        }
      }
    }

    if (bestFrameSimilarity < 0) {
      return {
        avgSpectrumSimilarity: 0,
        bestFrameSimilarity: 0,
        dominantFreqSimilarity: 0,
        durationRatio: 0,
        isSimilar: false,
        recordedRMS,
        similarityScore: 0,
      };
    }

    const recordedAvgSpectrum = calculateAverageSpectrum(recordedSpectra);
    const referenceAvgSpectrum = calculateAverageSpectrum(referenceSpectra);

    const smoothedRecordedAvg = smoothPowerSpectrum(recordedAvgSpectrum);
    const smoothedReferenceAvg = smoothPowerSpectrum(referenceAvgSpectrum);

    const recDominantFreqs = findDominantFrequencies(
      NUM_DOMINANT_FREQUENCIES_TO_FIND,
      smoothedRecordedAvg,
      targetSampleRate
    );
    const refDominantFreqs = findDominantFrequencies(
      NUM_DOMINANT_FREQUENCIES_TO_FIND,
      smoothedReferenceAvg,
      targetSampleRate
    );

    const frequencyTolerance = isMobile
      ? FREQUENCY_TOLERANCE_MOBILE
      : FREQUENCY_TOLERANCE_DESKTOP;

    const dominantFreqSimilarity = compareDominantFrequencies(
      refDominantFreqs,
      recDominantFreqs,
      frequencyTolerance
    );

    const avgSpectrumSimilarity = compareFrequencySpectra(
      smoothedReferenceAvg,
      smoothedRecordedAvg
    );

    const frameWeight = isMobile
      ? FRAME_SIMILARITY_WEIGHT_MOBILE
      : FRAME_SIMILARITY_WEIGHT_DESKTOP;
    const avgSpectrumWeight = isMobile
      ? AVG_SPECTRUM_WEIGHT_MOBILE
      : AVG_SPECTRUM_WEIGHT_DESKTOP;
    const dominantFreqWeight = isMobile
      ? DOMINANT_FREQUENCY_WEIGHT_MOBILE
      : DOMINANT_FREQUENCY_WEIGHT_DESKTOP;

    const combinedSimilarity =
      avgSpectrumSimilarity * avgSpectrumWeight +
      bestFrameSimilarity * frameWeight +
      dominantFreqSimilarity * dominantFreqWeight;

    let adjustedThreshold: number;

    if (isMobile) {
      if (mobileSimilarityThreshold !== undefined) {
        adjustedThreshold = mobileSimilarityThreshold;
      } else {
        const values = [
          similarityThreshold - MOBILE_SIMILARITY_THRESHOLD_ADJUSTMENT,
          MIN_MOBILE_SIMILARITY_THRESHOLD,
        ];
        let maxValue = 0;

        for (let i = 0; i < values.length; i += 1) {
          const v = values[i];

          if (v !== undefined && Number.isFinite(v) && v > maxValue) {
            maxValue = v;
          }
        }

        adjustedThreshold = maxValue;
      }
    } else {
      adjustedThreshold = similarityThreshold;
    }

    let minTopFreqMatches: number;

    if (isSafariBrowser) {
      minTopFreqMatches = MIN_TOP_FREQUENCY_MATCHES_SAFARI;
    } else if (isLowMobileThreshold) {
      minTopFreqMatches = MIN_TOP_FREQUENCY_MATCHES_LOW_MOBILE;
    } else if (isMobile) {
      minTopFreqMatches = MIN_TOP_FREQUENCY_MATCHES_MOBILE;
    } else {
      minTopFreqMatches = MIN_TOP_FREQUENCY_MATCHES_DESKTOP;
    }

    let adjustedMinTopFreqMatches = minTopFreqMatches;

    if (adjustedThreshold < LOW_MOBILE_THRESHOLD_CUTOFF) {
      adjustedMinTopFreqMatches = Math.max(
        MIN_TOP_FREQUENCY_MATCHES_FLOOR_LOW_THRESHOLD,
        Math.floor(
          minTopFreqMatches * LOW_THRESHOLD_TOP_FREQ_MATCHES_MULTIPLIER
        )
      );
    }

    const topFrequenciesMatch = validateTopFrequenciesMatch(
      refDominantFreqs,
      recDominantFreqs,
      frequencyTolerance,
      adjustedMinTopFreqMatches
    );

    if (!topFrequenciesMatch) {
      const isSafariHighSimilarity =
        isSafariBrowser &&
        combinedSimilarity >= SAFARI_HIGH_SIMILARITY_THRESHOLD &&
        dominantFreqSimilarity >=
          DOMINANT_FREQ_SIMILARITY_NORMAL_THRESHOLD_MOBILE;
      let minDominantFreqForBypass: number;

      if (adjustedThreshold < LOW_MOBILE_THRESHOLD_CUTOFF) {
        minDominantFreqForBypass =
          DOMINANT_FREQ_SIMILARITY_LOW_THRESHOLD_MOBILE;
      } else {
        minDominantFreqForBypass =
          DOMINANT_FREQ_SIMILARITY_NORMAL_THRESHOLD_MOBILE;
      }

      let minFrameSimilarityForBypass: number;

      if (adjustedThreshold < LOW_MOBILE_THRESHOLD_CUTOFF) {
        minFrameSimilarityForBypass = MIN_FRAME_SIMILARITY_LOW_MOBILE;
      } else if (isMobile) {
        minFrameSimilarityForBypass = MIN_FRAME_SIMILARITY_MOBILE;
      } else {
        minFrameSimilarityForBypass = MIN_FRAME_SIMILARITY_DESKTOP;
      }

      const hasVeryHighSimilarity =
        combinedSimilarity >=
          adjustedThreshold * VERY_HIGH_SIMILARITY_BYPASS_MULTIPLIER &&
        dominantFreqSimilarity >= minDominantFreqForBypass &&
        bestFrameSimilarity >= minFrameSimilarityForBypass;

      if (!isSafariHighSimilarity && !hasVeryHighSimilarity) {
        return {
          avgSpectrumSimilarity,
          bestFrameSimilarity,
          dominantFreqSimilarity,
          durationRatio: 0,
          isSimilar: false,
          recordedRMS,
          similarityScore: combinedSimilarity,
        };
      }
    }

    const minDuration = Math.min(
      processedReferenceBuffer.duration,
      processedRecordedBuffer.duration
    );
    const durationValues = [
      processedReferenceBuffer.duration,
      processedRecordedBuffer.duration,
    ];
    let maxDuration = 0;

    for (let i = 0; i < durationValues.length; i += 1) {
      const v = durationValues[i];

      if (v !== undefined && Number.isFinite(v) && v > maxDuration) {
        maxDuration = v;
      }
    }

    const durationRatio = minDuration / maxDuration;

    let minDurationRatio: number;

    if (isLowMobileThreshold) {
      minDurationRatio = MIN_DURATION_RATIO_LOW_MOBILE;
    } else if (isMobile) {
      minDurationRatio = MIN_DURATION_RATIO_MOBILE;
    } else {
      minDurationRatio = MIN_DURATION_RATIO_DESKTOP;
    }

    if (durationRatio < minDurationRatio) {
      if (adjustedThreshold < LOW_MOBILE_THRESHOLD_CUTOFF) {
        const adjustedMinDurationRatio = Math.max(
          MIN_DURATION_RATIO_FLOOR_LOW_THRESHOLD,
          minDurationRatio * LOW_THRESHOLD_DURATION_RATIO_MULTIPLIER
        );

        if (durationRatio < adjustedMinDurationRatio) {
          return {
            avgSpectrumSimilarity,
            bestFrameSimilarity,
            dominantFreqSimilarity,
            durationRatio,
            isSimilar: false,
            recordedRMS,
            similarityScore: combinedSimilarity,
          };
        }
      } else {
        return {
          avgSpectrumSimilarity,
          bestFrameSimilarity,
          dominantFreqSimilarity,
          durationRatio,
          isSimilar: false,
          recordedRMS,
          similarityScore: combinedSimilarity,
        };
      }
    }

    let minFrameSimilarity: number;

    if (isLowMobileThreshold) {
      minFrameSimilarity = MIN_FRAME_SIMILARITY_LOW_MOBILE;
    } else if (isMobile) {
      minFrameSimilarity = MIN_FRAME_SIMILARITY_MOBILE;
    } else {
      minFrameSimilarity = MIN_FRAME_SIMILARITY_DESKTOP;
    }

    let minDominantFreqSimilarity: number;

    if (isLowMobileThreshold) {
      minDominantFreqSimilarity = MIN_DOMINANT_FREQUENCY_SIMILARITY_LOW_MOBILE;
    } else if (isMobile) {
      minDominantFreqSimilarity = MIN_DOMINANT_FREQUENCY_SIMILARITY_MOBILE;
    } else {
      minDominantFreqSimilarity = MIN_DOMINANT_FREQUENCY_SIMILARITY_DESKTOP;
    }

    let minAvgSpectrumSimilarity: number;

    if (isLowMobileThreshold) {
      minAvgSpectrumSimilarity = MIN_AVG_SPECTRUM_SIMILARITY_LOW_MOBILE;
    } else if (isMobile) {
      minAvgSpectrumSimilarity = MIN_AVG_SPECTRUM_SIMILARITY_MOBILE;
    } else {
      minAvgSpectrumSimilarity = MIN_AVG_SPECTRUM_SIMILARITY_DESKTOP;
    }

    if (adjustedThreshold < LOW_MOBILE_THRESHOLD_CUTOFF) {
      const thresholdScale = Math.max(
        MIN_THRESHOLD_SCALE_FOR_LOW_THRESHOLD,
        adjustedThreshold / LOW_MOBILE_THRESHOLD_CUTOFF
      );
      minFrameSimilarity = Math.max(
        MIN_COMPONENT_SIMILARITY_FLOOR_LOW_THRESHOLD,
        minFrameSimilarity * thresholdScale
      );
      minDominantFreqSimilarity = Math.max(
        MIN_COMPONENT_SIMILARITY_FLOOR_LOW_THRESHOLD,
        minDominantFreqSimilarity * thresholdScale
      );
      minAvgSpectrumSimilarity = Math.max(
        MIN_COMPONENT_SIMILARITY_FLOOR_LOW_THRESHOLD,
        minAvgSpectrumSimilarity * thresholdScale
      );
    }

    const meetsMinimumRequirements =
      bestFrameSimilarity >= minFrameSimilarity &&
      dominantFreqSimilarity >= minDominantFreqSimilarity &&
      avgSpectrumSimilarity >= minAvgSpectrumSimilarity;

    const minOverallSimilarity = MIN_OVERALL_SIMILARITY;
    const hasMinimumTopFrequencyMatch = topFrequenciesMatch;

    let minDominantFreqForExactMatch: number;

    if (adjustedThreshold < LOW_MOBILE_THRESHOLD_CUTOFF) {
      if (isMobile) {
        minDominantFreqForExactMatch =
          DOMINANT_FREQ_SIMILARITY_LOW_THRESHOLD_MOBILE;
      } else {
        minDominantFreqForExactMatch =
          DOMINANT_FREQ_SIMILARITY_LOW_THRESHOLD_DESKTOP;
      }
    } else if (isMobile) {
      minDominantFreqForExactMatch =
        DOMINANT_FREQ_SIMILARITY_NORMAL_THRESHOLD_MOBILE;
    } else {
      minDominantFreqForExactMatch =
        DOMINANT_FREQ_SIMILARITY_NORMAL_THRESHOLD_DESKTOP;
    }

    const hasHighDominantFreqMatch =
      dominantFreqSimilarity >= minDominantFreqForExactMatch;

    let minFrameSimilarityForBypassCheck: number;

    if (adjustedThreshold < LOW_MOBILE_THRESHOLD_CUTOFF) {
      minFrameSimilarityForBypassCheck = MIN_FRAME_SIMILARITY_LOW_MOBILE;
    } else if (isMobile) {
      minFrameSimilarityForBypassCheck = MIN_FRAME_SIMILARITY_MOBILE;
    } else {
      minFrameSimilarityForBypassCheck = MIN_FRAME_SIMILARITY_DESKTOP;
    }

    const hasVeryHighSimilarityBypass =
      !hasMinimumTopFrequencyMatch &&
      combinedSimilarity >=
        adjustedThreshold * VERY_HIGH_SIMILARITY_BYPASS_MULTIPLIER &&
      dominantFreqSimilarity >=
        minDominantFreqForExactMatch * DOMINANT_FREQ_BYPASS_MULTIPLIER &&
      bestFrameSimilarity >= minFrameSimilarityForBypassCheck;
    const isSimilar =
      combinedSimilarity >= adjustedThreshold &&
      combinedSimilarity >= minOverallSimilarity &&
      meetsMinimumRequirements &&
      (hasMinimumTopFrequencyMatch || hasVeryHighSimilarityBypass) &&
      hasHighDominantFreqMatch;

    const result: IComparisonResult = {
      avgSpectrumSimilarity,
      bestFrameSimilarity,
      dominantFreqSimilarity,
      durationRatio,
      isSimilar,
      recordedRMS,
      similarityScore: combinedSimilarity,
    };

    return result;
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes('AudioContext is not supported')
    ) {
      throw new Error(browserNotSupportedErrorMessage);
    }

    return {
      avgSpectrumSimilarity: 0,
      bestFrameSimilarity: 0,
      dominantFreqSimilarity: 0,
      durationRatio: 0,
      isSimilar: false,
      recordedRMS: 0,
      similarityScore: 0,
    };
  } finally {
    if (audioContext) {
      await audioContext.close();
    }
  }
}
